import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Component for the Content and Creator Tools page
const CreatorToolsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('jingle-generator');

  // Function to render the appropriate tool content based on the active tab
  const renderToolContent = () => {
    switch (activeTab) {
      case 'jingle-generator':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4 dark:text-white">AI Jingle Generator</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Create short, royalty-free audio jingles from a simple text prompt.
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-500 mb-2">Coming soon!</p>
              <p>This tool will allow you to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Generate custom jingles for your brand or product</li>
                <li>Choose from different musical styles and moods</li>
                <li>Adjust length and complexity</li>
                <li>Download in multiple audio formats</li>
              </ul>
            </div>
          </div>
        );
      case 'link-shortener':
        return (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4 dark:text-white">AI Smart Link Shortener</h2>
            <p className="text-gray-600 mb-4">
              Creates human-readable short links based on the page content.
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-500 mb-2">Coming soon!</p>
              <p>This tool will allow you to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Generate memorable short links from long URLs</li>
                <li>Automatically analyze page content for relevant keywords</li>
                <li>Track link clicks and engagement</li>
                <li>Customize link appearance and expiration</li>
              </ul>
            </div>
          </div>
        );
      case 'pantry-chef':
        return (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4 dark:text-white">The Pantry Chef</h2>
            <p className="text-gray-600 mb-4">
              Generates a unique recipe based on a list of available ingredients.
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-500 mb-2">Coming soon!</p>
              <p>This tool will allow you to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Enter ingredients you have on hand</li>
                <li>Get personalized recipe suggestions</li>
                <li>Filter by dietary restrictions and preferences</li>
                <li>Save favorite recipes to your collection</li>
              </ul>
            </div>
          </div>
        );
      default:
        return <div className="dark:text-white">Select a tool to get started</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <h1 className="text-3xl font-bold">Content and Creator Tools</h1>
            </div>
            <p className="text-blue-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Tools for content creators, marketers, and social media professionals
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Tool Selection Tabs */}
          <div className="flex flex-wrap mb-6 border-b dark:border-gray-700">
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'jingle-generator' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-300'}`}
              onClick={() => setActiveTab('jingle-generator')}
            >
              Jingle Generator
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'link-shortener' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-300'}`}
              onClick={() => setActiveTab('link-shortener')}
            >
              Smart Link Shortener
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'pantry-chef' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-300'}`}
              onClick={() => setActiveTab('pantry-chef')}
            >
              Pantry Chef
            </button>
          </div>
          
          {/* Tool Content */}
          {renderToolContent()}
        </div>
      </div>
    </div>
  );
};

export default CreatorToolsPage;
